import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { InspectionService } from '../inspection.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class FlagManagerService {
  private selectedFlagIdsSubject = new BehaviorSubject<number[]>([]);
  private selectAllSubject = new BehaviorSubject<boolean>(false);
  private draggingSubject = new BehaviorSubject<boolean>(false);
  private draggingFlagSubject = new BehaviorSubject<any>(null);
  private statusChangeSubject = new BehaviorSubject<{ statusId: number; inspectionId?: number } | null>(null);
  private excludedGroupsSubject = new BehaviorSubject<any[]>([]);
  private includedGroupsSubject = new BehaviorSubject<any[]>([]);
  private flagPictureSubject = new BehaviorSubject<any>(null);
  private lettersSubject = new BehaviorSubject<any[]>([]);
  private flagsUpdatedSubject = new BehaviorSubject<void>(undefined);

  constructor(
    private inspectionService: InspectionService,
    private toastr: ToastrService,
  ) {}

  // Get selected flag IDs as observable
  getSelectedFlagIds(): Observable<number[]> {
    return this.selectedFlagIdsSubject.asObservable();
  }

  // Set selected flag IDs
  setSelectedFlagIds(flagIds: number[]): void {
    this.selectedFlagIdsSubject.next(flagIds);
  }

  // Get select all state as observable
  getSelectAllState(): Observable<boolean> {
    return this.selectAllSubject.asObservable();
  }

  // Toggle select all state
  toggleSelectAll(): void {
    const currentState = this.selectAllSubject.value;
    this.selectAllSubject.next(!currentState);
  }

  // Select all flags
  selectAllFlags(allFlagIds: number[]): void {
    this.setSelectedFlagIds([...allFlagIds]);
    this.selectAllSubject.next(true);
  }

  // Deselect all flags
  deselectAllFlags(): void {
    this.setSelectedFlagIds([]);
    this.selectAllSubject.next(false);
  }

  // Add single flag to selection
  addFlagToSelection(flagId: number): void {
    const currentIds = this.selectedFlagIdsSubject.value;
    if (!currentIds.includes(flagId)) {
      this.setSelectedFlagIds([...currentIds, flagId]);
    }
  }

  // Remove single flag from selection
  removeFlagFromSelection(flagId: number): void {
    const currentIds = this.selectedFlagIdsSubject.value;
    this.setSelectedFlagIds(currentIds.filter(id => id !== flagId));
  }

  // Dragging state
  getDragging() {
    return this.draggingSubject.asObservable();
  }
  setDragging(isDragging: boolean): void {
    this.draggingSubject.next(isDragging);
  }

  getDraggingFlag() {
    return this.draggingFlagSubject.asObservable();
  }
  setDraggingFlag(flag: any | null) {
    this.draggingFlagSubject.next(flag);
  }

  // Status change management
  getStatusChange(): Observable<{ statusId: number; inspectionId?: number } | null> {
    return this.statusChangeSubject.asObservable();
  }

  async updateUnderwritingStatus(inspectionId: number, statusId: number): Promise<void> {
    try {
      await this.inspectionService.setUnderwritingStatus(inspectionId, statusId as any);
      this.toastr.success('Underwriting status updated!');
      this.statusChangeSubject.next({ statusId, inspectionId });
    } catch (error) {
      this.toastr.error('Failed to update underwriting status');
      console.error('Error updating underwriting status:', error);
      throw error;
    }
  }

  // Flag picture update
  getFlagPictureUpdates(): Observable<any> {
    return this.flagPictureSubject.asObservable();
  }

  setFlagPictureUpdates(picture: any): void {
    this.flagPictureSubject.next(picture);
  }

  // Group separation methods
  getExcludedGroups(): Observable<any[]> {
    return this.excludedGroupsSubject.asObservable();
  }

  getIncludedGroups(): Observable<any[]> {
    return this.includedGroupsSubject.asObservable();
  }

  separateGroups(groups: any[]): void {
    const excludedGroups: any[] = [];
    const includedGroups: any[] = [];

    if (!groups || !Array.isArray(groups)) {
      this.excludedGroupsSubject.next(excludedGroups);
      this.includedGroupsSubject.next(includedGroups);
      return;
    }

    groups.forEach(group => {
      const flags = group.flags || [];
      const allExcluded = flags.length === 0 || flags.every(flag => flag.ignore);
      group.excluded = allExcluded;
      if (allExcluded) {
        excludedGroups.push(group);
      } else {
        includedGroups.push(group);
      }
    });

    this.excludedGroupsSubject.next(excludedGroups);
    this.includedGroupsSubject.next(includedGroups);
  }

  getLetters(): Observable<any[]> {
    return this.lettersSubject.asObservable();
  }

  setLetters(letters: any[]): void {
    this.lettersSubject.next(letters || []);
  }

  getLatestLetter(): any | null {
    const letters = this.lettersSubject.value;
    if (!letters || letters.length === 0) return null;
    return letters[0];
  }

  async loadLetters(inspectionId: number): Promise<any[]> {
    try {
      const list = await this.inspectionService.listLetters(inspectionId);
      const letters = list
        .filter(letter => !letter.preview)
        .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
        .map(letter => ({
          ...letter,
          expandedUrl: location.origin + '/recommendation-letter/expanded/' + encodeURIComponent(letter.hash),
        }));

      // Store letters in the service
      this.setLetters(letters);
      return letters;
    } catch (error) {
      console.error('Error loading letters:', error);
      return [];
    }
  }

  // Flags updated notification
  getFlagsUpdated(): Observable<void> {
    return this.flagsUpdatedSubject.asObservable();
  }

  notifyFlagsUpdated(): void {
    this.flagsUpdatedSubject.next(undefined);
  }

  // Flag order update notification
  private flagOrderUpdatedSubject = new BehaviorSubject<{ flagId: number; newOrder: number } | null>(null);

  getFlagOrderUpdated(): Observable<{ flagId: number; newOrder: number } | null> {
    return this.flagOrderUpdatedSubject.asObservable();
  }

  notifyFlagOrderUpdated(flagId: number, newOrder: number): void {
    this.flagOrderUpdatedSubject.next({ flagId, newOrder });
  }
}
